// src/components/GameDetailModal.js
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AmazonSearch from './AmazonSearch';
import EbayWidget from './EbayWidget'; 
import VoteButtons from './VoteButtons';
import { getGameSlug } from '../utils/slugHelper';

const mapWeightToDifficulty = (weight) => {
  if (!weight) return '';
  
  if (weight === 'Light' || weight === 'Medium Light' || weight === 'Light Medium') {
    return 'Easy';
  } else if (weight === 'Medium') {
    return 'Medium';
  } else if (weight === 'Medium Heavy' || weight === 'Heavy') {
    return 'Hard';
  }
  
  return '';
};

const GameDetailModal = ({ game, onClose, onVote, showLogin, darkMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Don't do anything if there's no game
    if (!game) return;
    
    // Save the current overflow style
    const originalStyle = window.getComputedStyle(document.body).overflow;
    // Store the current path to use in cleanup
    const currentPath = location.pathname;
    
    // Prevent scrolling on mount
    document.body.style.overflow = 'hidden';
    
    // Update the URL without causing a page reload
    // The key is using state.backgroundLocation to preserve the search results
    navigate(`/game/${getGameSlug(game)}`, { 
      state: { 
        modal: true, 
        backgroundLocation: currentPath
      },
      replace: true 
    });
    
    // Re-enable scrolling on unmount but don't navigate (parent component handles this)
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [game, navigate, location]);

  // Early return if no game
  if (!game) return null;

  // Dark mode class variables
  const bgClass = darkMode ? 'bg-gray-900' : 'bg-white';
  const textClass = darkMode ? 'text-gray-100' : 'text-gray-700';
  const borderClass = darkMode ? 'border-gray-700' : 'border-gray-200';
  const headerBgClass = darkMode ? 'bg-gray-800' : 'bg-white';
  const buttonTextClass = darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900';
  const themeClass = darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800';
  const mechanicClass = darkMode ? 'bg-green-900 text-green-100' : 'bg-green-100 text-green-800';

  // Handle overlay click - close the modal only if the click is on the overlay
  const handleOverlayClick = (e) => {
    // Only close if the click is directly on the overlay, not its children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleVote = (gameId, voteValue, upvotes, downvotes) => {
    if (onVote) {
      onVote(gameId, voteValue, upvotes, downvotes);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-hidden"
      onClick={handleOverlayClick}
    >
      <div className={`${bgClass} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
        {/* Header with back button */}
        <div className={`sticky top-0 ${headerBgClass} border-b ${borderClass} px-6 py-4 flex items-center justify-between`}>
          <button 
            onClick={onClose}
            className={`flex items-center ${buttonTextClass} focus:outline-none`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to search results
          </button>
          <div>
            <VoteButtons 
              game={game}
              onVote={handleVote}
              showLogin={showLogin}
            />
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Game image with overlay info */}
          <div className="w-full max-h-[240px] mb-4 overflow-hidden rounded-md bg-gray-700 flex items-center justify-center relative">
            <img 
              src={game.thumbnail ? `/media/${game.thumbnail}` : "/media/placeholder.gif"} 
              alt={`${game.title} board game`}
              className="object-cover"
            />
            {/* Title overlay at the top */}
            <div className="absolute top-0 left-0 right-0 bg-black bg-opacity-70 text-white px-3 py-2">
              <h2 className="text-xl font-bold">{game.title}</h2>
            </div>
            {/* Stats overlay at the bottom */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-between bg-black bg-opacity-70 text-white px-3 py-2">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>{game.min_players === game.max_players ? game.min_players : `${game.min_players}-${game.max_players}`}</span>
              </div>
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{game.min_play_time === game.max_play_time ? `${game.min_play_time} min` : `${game.min_play_time}-${game.max_play_time} min`}</span>
              </div>
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{mapWeightToDifficulty(game.weight)}</span>
              </div>
            </div>
          </div>

          {/* Full description */}
          <div className="mb-6">
            <p className={`${textClass} whitespace-pre-line`}>{game.description || game.description_short}</p>
          </div>
          
          {/* Game details */}
          <div className="mb-6">
            <div className="space-y-3">
              <div>
                <span className={`font-semibold block mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Themes:</span>
                <div className="flex flex-wrap gap-2">
                  {game.themes?.map(theme => (
                    <span key={theme} className={`${themeClass} px-2 py-1 rounded-full text-sm`}>
                      {theme}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <span className={`font-semibold block mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Mechanics:</span>
                <div className="flex flex-wrap gap-2">
                  {game.mechanics?.map(mechanic => (
                    <span key={mechanic} className={`${mechanicClass} px-2 py-1 rounded-full text-sm`}>
                      {mechanic}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Amazon Search Section */}
          <AmazonSearch gameTitle={game.title} />
          <EbayWidget gameTitle={game.title} />
        </div>
      </div>
    </div>
  );
};

export default GameDetailModal;